import React from 'react';
import { Alert } from 'antd';

import { isGlobal } from '../../utils/collectionKinds';
import PublishedBy from '../publishers/PublishedBy';
import { formatDisplayDate } from '../../utils/dateDisplay';
import { safeHref } from '../../utils/safeHref';
import ExternalLink from '../common/ExternalLink';

// KPR-386. A reference collection says where it comes from, under what terms,
// what it is not, and when somebody last checked it. The words are the
// owner's, shown to every account, so they only ever reach the page as React
// text children and anchors built here: never innerHTML, never markdown. The
// API already refuses markup-looking text; this is the second wall, not the
// first.

const URL_IN_TEXT = /https?:\/\/[^\s<>"']+/g;
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

// An http(s) address, or null (utils/safeHref.js — shared with the
// publisher's line, KPR-385). Re-exported: callers import it from here.
export { safeHref };

function Anchor({ href, children }) {
	return <ExternalLink href={href} rel="noopener noreferrer nofollow">{children}</ExternalLink>;
}

/** Plain text in which bare http(s) addresses become links. */
export function LinkedText({ text }) {
	const source = typeof text === 'string' ? text : '';
	const parts = [];
	let last = 0;
	let match;
	URL_IN_TEXT.lastIndex = 0;
	// eslint-disable-next-line no-cond-assign
	while ((match = URL_IN_TEXT.exec(source))) {
		// "see https://x.test/a." ends the sentence, not the address.
		const raw = match[0].replace(TRAILING_PUNCTUATION, '');
		const href = safeHref(raw);
		if (match.index > last) parts.push(source.slice(last, match.index));
		parts.push(href ? <Anchor key={match.index} href={href}>{raw}</Anchor> : raw);
		if (raw.length < match[0].length) parts.push(match[0].slice(raw.length));
		last = match.index + match[0].length;
	}
	if (last < source.length) parts.push(source.slice(last));
	return <>{parts}</>;
}

/** `{ text, url? }` as text, linked when it has an address. */
export function Attribution({ value }) {
	if (!value || !value.text) return null;
	const href = safeHref(value.url);
	return href ? <Anchor href={href}>{value.text}</Anchor> : <>{value.text}</>;
}

/**
 * The disclaimer, on the collection's home and on every record of it. Shown
 * to everyone who reads a global collection, its owner included: it is part of
 * what the collection says, not a consumer-only banner. Not dismissible, for
 * the same reason the live-edit notice is not.
 */
export function GlobalDisclaimer({ collection, className = '' }) {
	if (!collection || !isGlobal(collection)) return null;
	const text = collection.publication?.listing?.disclaimer;
	if (!text) return null;
	return (
		<Alert
			type="info"
			role="note"
			data-testid="global-disclaimer"
			className={['global-disclaimer', className].filter(Boolean).join(' ')}
			message={<span className="global-disclaimer__text"><LinkedText text={text} /></span>}
		/>
	);
}

/** The About facts: Publisher, Source, License and "Last reviewed", each only when there is something to say. */
export function ListingFacts({ collection, Fact }) {
	if (!collection || !isGlobal(collection)) return null;
	const p = collection.publication || {};
	const listing = p.listing || {};
	return (
		<>
			{collection.publisher ? (
				<Fact label="Publisher" testId="listing-publisher">
					<PublishedBy publisher={collection.publisher} bare link />
				</Fact>
			) : null}
			{listing.source?.text ? (
				<Fact label="Source" testId="listing-source">
					<Attribution value={listing.source} />
					{p.lastReviewedAt ? <span className="meta-line">{` · Last reviewed ${formatDisplayDate(p.lastReviewedAt)}`}</span> : null}
				</Fact>
			) : p.lastReviewedAt ? (
				<Fact label="Last reviewed" testId="listing-reviewed">{formatDisplayDate(p.lastReviewedAt)}</Fact>
			) : null}
			{listing.license?.text ? (
				<Fact label="License" testId="listing-license"><Attribution value={listing.license} /></Fact>
			) : null}
		</>
	);
}
